import React from "react";
import { toBerlinISODate } from "components/Calendar";
import { formatPrice } from "constants/massages";
import { statusLabel, translateServiceLabel, isManualRevenueCalendarItem, formatEuro } from "./formatters";
import {
  SLOT_HEIGHT_PX,
  buildTimeSlots,
  resolveSchedule,
  isDayClosed,
  layoutDayBookings,
  eventLaneStyle,
  formatDayHeader,
  dayKey,
} from "./scheduleGrid";

const TeamScheduleGrid = ({
  days,
  daysMap = {},
  scheduleBrand = "tawan-spa",
  dateLocale,
  t,
  selectedKey,
  today,
  onSelectDay,
  onBookingSelect,
  onRevenueSelect,
  loading,
}) => {
  const schedule = resolveSchedule(scheduleBrand);
  const slots = buildTimeSlots(schedule.openHour, schedule.closeHour);
  const bodyHeight = slots.length * SLOT_HEIGHT_PX;
  const colCount = days.length;
  const isWeek = colCount > 1;

  if (loading) {
    return <p className="team-muted team-schedule-loading">{t("common.loading")}</p>;
  }

  return (
    <div
      className={`team-schedule${isWeek ? " team-schedule--week" : " team-schedule--day"}`}
      style={{ "--schedule-cols": colCount }}
    >
      <div className="team-schedule-header-row">
        <div className="team-schedule-gutter" aria-hidden="true" />
        {days.map((day) => {
          const key = dayKey(day);
          const closed = isDayClosed(day, scheduleBrand);
          const isToday = key === toBerlinISODate(today);
          const isSelected = key === selectedKey;
          const { primary, secondary } = formatDayHeader(day, dateLocale, isWeek ? "week" : "day");
          const count = (daysMap[key] || []).length;

          return (
            <button
              key={key}
              type="button"
              className={[
                "team-schedule-day-header",
                closed ? "is-closed" : "",
                isToday ? "is-today" : "",
                isSelected ? "is-selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onSelectDay?.(day)}
              disabled={!onSelectDay}
            >
              {isWeek ? (
                <>
                  <span className="team-schedule-day-name">{primary}</span>
                  <span className="team-schedule-day-date">{secondary}</span>
                  {!closed && count > 0 && (
                    <span className="team-schedule-day-count">{count}</span>
                  )}
                </>
              ) : (
                <span className="team-schedule-day-name">{primary}</span>
              )}
              {closed && !isWeek && (
                <span className="team-schedule-closed-label">{t("calendar.closedDay")}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="team-schedule-body">
        <div className="team-schedule-times" style={{ height: bodyHeight }}>
          {slots.map((slot) => (
            <div
              key={slot.label}
              className="team-schedule-time"
              style={{ height: SLOT_HEIGHT_PX }}
            >
              {slot.label}
            </div>
          ))}
        </div>

        <div className="team-schedule-columns">
          {days.map((day) => {
            const key = dayKey(day);
            const closed = isDayClosed(day, scheduleBrand);
            const dayItems = daysMap[key] || [];
            const bookings = dayItems.filter((item) => !isManualRevenueCalendarItem(item));
            const revenueItems = dayItems.filter(isManualRevenueCalendarItem);

            return (
              <div
                key={key}
                className={[
                  "team-schedule-col",
                  closed ? "is-closed" : "",
                  key === selectedKey ? "is-selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {revenueItems.length > 0 && (
                  <div className="team-schedule-revenue-strip">
                    {revenueItems.map((entry) => (
                      <button
                        key={entry._id}
                        type="button"
                        className="team-schedule-revenue-item"
                        onClick={() => onRevenueSelect?.(entry)}
                      >
                        <span className="team-schedule-revenue-label">
                          {t("calendar.revenueEntry")}
                        </span>
                        <span className="team-schedule-revenue-amount">
                          {formatEuro(entry.priceCents)}
                        </span>
                        {entry.massageType && (
                          <span className="team-muted team-schedule-revenue-type">
                            {entry.massageType}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <div
                  className="team-schedule-col-grid"
                  style={{ height: bodyHeight }}
                >
                {slots.map((slot) => (
                  <div
                    key={`${key}-${slot.label}`}
                    className="team-schedule-cell"
                    style={{ height: SLOT_HEIGHT_PX }}
                  />
                ))}

                {!closed &&
                  layoutDayBookings(bookings, schedule.openHour).map(
                    ({ booking: b, top, height, column, totalColumns }) => (
                      <button
                        key={b._id}
                        type="button"
                        className="team-schedule-event team-schedule-event--clickable"
                        style={{
                          top,
                          height,
                          ...eventLaneStyle(column, totalColumns),
                        }}
                        data-overlap-lanes={totalColumns}
                        title={`${translateServiceLabel(t, b)} — ${b.customer?.firstName || ""} ${b.customer?.lastName || ""}`}
                        onClick={() => onBookingSelect?.(b)}
                      >
                        <span className="team-schedule-event-line">
                          <time dateTime={b.start}>
                            {new Date(b.start).toLocaleTimeString(dateLocale, {
                              hour: "2-digit",
                              minute: "2-digit",
                              timeZone: "Europe/Berlin",
                            })}
                          </time>
                          <span className="team-schedule-event-title">
                            {translateServiceLabel(t, b)}
                          </span>
                        </span>
                        {height >= SLOT_HEIGHT_PX * 1.5 && (
                          <span className="team-muted team-schedule-event-customer">
                            {b.customer?.firstName} {b.customer?.lastName}
                          </span>
                        )}
                        {height >= SLOT_HEIGHT_PX * 1.5 && (
                          <span className="team-schedule-event-meta">
                            <span className="badge">{statusLabel(t, b.status)}</span>
                            {b.priceCents > 0 && (
                              <span className="badge">{formatPrice(b.priceCents)}</span>
                            )}
                          </span>
                        )}
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TeamScheduleGrid;
